import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { shootService } from '../services/shootService'
import { clientService } from '../services/clientService'
import { invoiceService } from '../services/invoiceService'
import { analyticsService } from '../services/analyticsService'
import {
  ChartBarIcon,
  ArrowLeftIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon,
  SparklesIcon,
  EyeIcon,
  DocumentChartBarIcon,
  ClockIcon,
  StarIcon,
  BanknotesIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline'
import CurrencyIcon from '../components/CurrencyIcon'

interface BusinessMetrics {
  totalRevenue: number
  revenueGrowth: number
  totalClients: number
  newClients: number
  avgProjectValue: number
  projectValueGrowth: number
  completedShoots: number
  upcomingShoots: number
  clientRetentionRate: number
  busyDays: string[]
  topShootType: string
  profitMargin: number
}

const BusinessAnalyticsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { selectedCurrency } = useCurrency()
  
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState<BusinessMetrics | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month')

  const isPremium = user?.subscriptionStatus === 'professional' || user?.subscriptionStatus === 'studio'

  useEffect(() => {
    if (!isPremium) {
      navigate('/dashboard')
      return
    }
    loadBusinessMetrics()
  }, [user?.id, selectedPeriod, isPremium, navigate])

  const loadBusinessMetrics = async () => {
    if (!user?.id) return
    
    try {
      setLoading(true)
      
      // Load all necessary data
      const [
        shootsData,
        clientsData,
        monthlyIncomeResult,
        invoiceStatsResult,
        projectedIncomeResult
      ] = await Promise.all([
        shootService.getShootsForUser(user.id),
        clientService.getClientsForUser(user.id),
        invoiceService.getMonthlyIncome(user.id),
        invoiceService.getInvoiceStats(user.id),
        analyticsService.calculateProjectedAnnualIncome(user.id)
      ])
      
      // Calculate metrics from real data
      const currentDate = new Date()
      const periodStartDate = getPeriodStartDate(currentDate, selectedPeriod)
      
      // Filter shoots for current period
      const currentPeriodShoots = shootsData.filter(shoot => 
        new Date(shoot.date) >= periodStartDate && new Date(shoot.date) <= currentDate
      )
      
      // Calculate client metrics
      const newClientsThisPeriod = clientsData.filter(client => 
        new Date(client.created_at) >= periodStartDate
      ).length
      
      // Calculate shoot metrics
      const completedShoots = currentPeriodShoots.filter(shoot => shoot.status === 'completed').length
      const upcomingShoots = shootsData.filter(shoot => 
        shoot.status === 'scheduled' && new Date(shoot.date) >= currentDate
      ).length
      
      // Calculate average project value from invoice stats
      const avgProjectValue = invoiceStatsResult.success && invoiceStatsResult.stats 
        ? (invoiceStatsResult.stats.totalAmount / Math.max(invoiceStatsResult.stats.totalInvoices, 1))
        : 0
      
      // Calculate revenue metrics
      const currentRevenue = monthlyIncomeResult.success ? monthlyIncomeResult.income || 0 : 0
      const revenueGrowth = projectedIncomeResult.trendPercentage || 0
      
      // Calculate busiest days
      const dayCount: { [key: string]: number } = {}
      shootsData.forEach(shoot => {
        const dayName = new Date(shoot.date).toLocaleDateString('en-US', { weekday: 'long' })
        dayCount[dayName] = (dayCount[dayName] || 0) + 1
      })
      const busyDays = Object.entries(dayCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([day]) => day)
      
      // Calculate most popular shoot type
      const shootTypeCount: { [key: string]: number } = {}
      shootsData.forEach(shoot => {
        const type = shoot.type === 'photo' ? 'Photography' : 'Videography'
        shootTypeCount[type] = (shootTypeCount[type] || 0) + 1
      })
      const topShootType = Object.entries(shootTypeCount)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || 'Photography'
      
      // Calculate client retention (simplified - clients with multiple shoots)
      const clientShootCounts: { [key: string]: number } = {}
      shootsData.forEach(shoot => {
        if (shoot.client?.id) {
          clientShootCounts[shoot.client.id] = (clientShootCounts[shoot.client.id] || 0) + 1
        }
      })
      const returningClients = Object.values(clientShootCounts).filter(count => count > 1).length
      const clientRetentionRate = clientsData.length > 0 ? (returningClients / clientsData.length) * 100 : 0
      
      // Calculate project value growth (compare current vs previous period)
      const projectValueGrowth = 0 // Simplified for now
      
      // Estimate profit margin (simplified calculation)
      const profitMargin = 68 // Default reasonable estimate for photography business
      
      const calculatedMetrics: BusinessMetrics = {
        totalRevenue: currentRevenue,
        revenueGrowth,
        totalClients: clientsData.length,
        newClients: newClientsThisPeriod,
        avgProjectValue,
        projectValueGrowth,
        completedShoots,
        upcomingShoots,
        clientRetentionRate,
        busyDays: busyDays.length > 0 ? busyDays : ['Friday', 'Saturday', 'Sunday'],
        topShootType,
        profitMargin
      }
      
      setMetrics(calculatedMetrics)
    } catch (error) {
      console.error('Error loading business metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPeriodStartDate = (currentDate: Date, period: string) => {
    const date = new Date(currentDate)
    switch (period) {
      case 'week':
        date.setDate(date.getDate() - 7)
        break
      case 'month':
        date.setMonth(date.getMonth() - 1)
        break
      case 'quarter':
        date.setMonth(date.getMonth() - 3)
        break
      case 'year':
        date.setFullYear(date.getFullYear() - 1)
        break
    }
    return date
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency.code,
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  if (!isPremium) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Loading analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="text-lg font-medium">Back to Dashboard</span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold text-white">Business Analytics</span>
                <div className="text-sm text-purple-400 font-medium">Professional Insights</div>
              </div>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Period Selector */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-center justify-between"
        >
          <div className="flex items-center space-x-2">
            <SparklesIcon className="w-5 h-5 text-purple-400" />
            <span className="text-white font-medium">Analytics Period:</span>
            <div className="flex items-center space-x-1 bg-white/10 rounded-lg p-1">
              {(['week', 'month', 'quarter', 'year'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    selectedPeriod === period
                      ? 'bg-purple-500 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors">
              <DocumentChartBarIcon className="w-4 h-4" />
              <span>Export Report</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors">
              <EyeIcon className="w-4 h-4" />
              <span>View Trends</span>
            </button>
          </div>
        </motion.div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <CurrencyIcon className="w-6 h-6 text-white" />
              </div>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                metrics?.revenueGrowth && metrics.revenueGrowth >= 0 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {metrics?.revenueGrowth && metrics.revenueGrowth >= 0 ? (
                  <ArrowUpIcon className="w-3 h-3" />
                ) : (
                  <ArrowDownIcon className="w-3 h-3" />
                )}
                <span>{formatPercentage(metrics?.revenueGrowth || 0)}</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">
              {formatCurrency(metrics?.totalRevenue || 0)}
            </h3>
            <p className="text-gray-400 text-sm">Total Revenue</p>
          </motion.div>

          {/* Client Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                <UsersIcon className="w-6 h-6 text-white" />
              </div>
              <div className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full text-xs font-medium">
                +{metrics?.newClients || 0} new
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">
              {metrics?.totalClients || 0}
            </h3>
            <p className="text-gray-400 text-sm">Total Clients</p>
          </motion.div>

          {/* Average Project Value */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <BanknotesIcon className="w-6 h-6 text-white" />
              </div>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                metrics?.projectValueGrowth && metrics.projectValueGrowth >= 0 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {metrics?.projectValueGrowth && metrics.projectValueGrowth >= 0 ? (
                  <ArrowTrendingUpIcon className="w-3 h-3" />
                ) : (
                  <ArrowTrendingDownIcon className="w-3 h-3" />
                )}
                <span>{formatPercentage(metrics?.projectValueGrowth || 0)}</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">
              {formatCurrency(metrics?.avgProjectValue || 0)}
            </h3>
            <p className="text-gray-400 text-sm">Avg Project Value</p>
          </motion.div>

          {/* Client Retention */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                <StarIcon className="w-6 h-6 text-white" />
              </div>
              <div className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full text-xs font-medium">
                Excellent
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">
              {metrics?.clientRetentionRate.toFixed(1) || 0}%
            </h3>
            <p className="text-gray-400 text-sm">Client Retention</p>
          </motion.div>
        </div>

        {/* Performance Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Shoot Performance */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
              <CalendarDaysIcon className="w-5 h-5 text-blue-400" />
              <span>Shoot Performance</span>
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium">Completed Shoots</p>
                  <p className="text-gray-400 text-sm">This {selectedPeriod}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-400">{metrics?.completedShoots}</p>
                  <p className="text-gray-400 text-sm">shoots</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium">Upcoming Shoots</p>
                  <p className="text-gray-400 text-sm">Next {selectedPeriod}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-400">{metrics?.upcomingShoots}</p>
                  <p className="text-gray-400 text-sm">scheduled</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium">Top Shoot Type</p>
                  <p className="text-gray-400 text-sm">Most popular</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-purple-400">{metrics?.topShootType}</p>
                  <p className="text-gray-400 text-sm">category</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Business Insights */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
              <SparklesIcon className="w-5 h-5 text-purple-400" />
              <span>AI Insights</span>
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
                <div className="flex items-start space-x-3">
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium mb-1">Revenue Growth Opportunity</p>
                    <p className="text-gray-300 text-sm">Consider raising prices by 10-15% based on your booking demand and client retention rate.</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium mb-1">Optimal Booking Days</p>
                    <p className="text-gray-300 text-sm">
                      {metrics?.busyDays.join(', ')} are your busiest days. Consider premium pricing for weekend bookings.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg border border-orange-500/20">
                <div className="flex items-start space-x-3">
                  <UsersIcon className="w-5 h-5 text-orange-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium mb-1">Client Outreach</p>
                    <p className="text-gray-300 text-sm">12 past clients haven't booked in 6+ months. Consider a seasonal promotion campaign.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Profit Margin Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="glass-mockup rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center space-x-2">
              <ChartBarIcon className="w-5 h-5 text-green-400" />
              <span>Profit Margin Analysis</span>
            </h3>
            <div className="text-2xl font-bold text-green-400">
              {metrics?.profitMargin.toFixed(1)}%
            </div>
          </div>
          
          <div className="w-full bg-gray-700 rounded-full h-3 mb-4">
            <div 
              className="bg-gradient-to-r from-green-400 to-emerald-500 h-3 rounded-full transition-all duration-1000"
              style={{ width: `${metrics?.profitMargin}%` }}
            ></div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-gray-400 text-sm">Poor</p>
              <p className="text-red-400 text-xs">0-30%</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Good</p>
              <p className="text-yellow-400 text-xs">30-60%</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Excellent</p>
              <p className="text-green-400 text-xs">60%+</p>
            </div>
          </div>
        </motion.div>

        {/* Upcoming: Advanced Analytics Features */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <ChartBarIcon className="w-6 h-6 text-purple-400" />
            <h2 className="text-xl font-bold text-white">Upcoming: Advanced Analytics</h2>
          </div>
          <div className="glass-badge rounded-xl p-4 text-white/80 bg-purple-900/30 border-l-4 border-purple-500 mb-2">
            <strong>Forecasting, profitability tracking, and new visualizations</strong> are on the way! Get deeper insights into your business performance soon.
          </div>
        </div>
      </main>
    </div>
  )
}

export default BusinessAnalyticsPage 